import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../../services/api.service';
import './ExhibitionForm.css';

const ExhibitionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    event_name: '',
    event_caption: '',
    event_location: '',
    event_date: '',
    event_time: '',
    status: 1
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isEditMode) {
      const fetchExhibition = async () => {
        try {
          setFetchLoading(true);
          setError(null);
          const response = await apiService.getAdminExhibitionById(id);
          if (response.data.success) {
            const exhibition = response.data.data;
            setFormData({
              event_name: exhibition.event_name || '',
              event_caption: exhibition.event_caption || '',
              event_location: exhibition.event_location || '',
              event_date: exhibition.event_date || '',
              event_time: exhibition.event_time || '',
              status: exhibition.status || 1
            });
          } else {
            setError('Failed to load exhibition');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to load exhibition');
          console.error('Exhibition fetch error:', err);
        } finally {
          setFetchLoading(false);
        }
      };
      fetchExhibition();
    }
  }, [id, isEditMode]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.event_name.trim()) {
      newErrors.event_name = 'Event name is required';
    } else if (formData.event_name.trim().length < 3) {
      newErrors.event_name = 'Event name must be at least 3 characters';
    }

    if (!formData.event_location.trim()) {
      newErrors.event_location = 'Location/venue is required';
    }

    if (!formData.event_date) {
      newErrors.event_date = 'Event date is required';
    }

    if (!formData.event_time) {
      newErrors.event_time = 'Event time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const finalValue = type === 'number' ? parseInt(value) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      const response = isEditMode
        ? await apiService.updateExhibition(id, formData)
        : await apiService.createExhibition(formData);

      if (response.data.success) {
        setSuccessMessage(isEditMode ? 'Exhibition updated successfully!' : 'Exhibition created successfully!');
        setTimeout(() => {
          navigate('/admin/exhibitions');
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to save exhibition');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save exhibition');
      console.error('Exhibition save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/exhibitions');
  };

  if (fetchLoading) {
    return (
      <div className="exhibition-form-container">
        <div className="loading">Loading exhibition...</div>
      </div>
    );
  }

  return (
    <div className="exhibition-form-container">
      <div className="exhibition-form-header">
        <h2>{isEditMode ? 'Edit Exhibition' : 'Add New Exhibition'}</h2>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="exhibition-form">
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="event_name">
              Event Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="event_name"
              name="event_name"
              value={formData.event_name}
              onChange={handleChange}
              className={errors.event_name ? 'error' : ''}
              placeholder="Enter event name"
            />
            {errors.event_name && (
              <span className="error-message">{errors.event_name}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="event_caption">Caption</label>
            <textarea
              id="event_caption"
              name="event_caption"
              value={formData.event_caption}
              onChange={handleChange}
              rows="3"
              placeholder="Enter event caption or description (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="event_location">
              Location/Venue <span className="required">*</span>
            </label>
            <input
              type="text"
              id="event_location"
              name="event_location"
              value={formData.event_location}
              onChange={handleChange}
              className={errors.event_location ? 'error' : ''}
              placeholder="Enter event location"
            />
            {errors.event_location && (
              <span className="error-message">{errors.event_location}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="event_date">
                Event Date <span className="required">*</span>
              </label>
              <input
                type="date"
                id="event_date"
                name="event_date"
                value={formData.event_date}
                onChange={handleChange}
                className={errors.event_date ? 'error' : ''}
              />
              {errors.event_date && (
                <span className="error-message">{errors.event_date}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="event_time">
                Event Time <span className="required">*</span>
              </label>
              <input
                type="time"
                id="event_time"
                name="event_time"
                value={formData.event_time}
                onChange={handleChange}
                className={errors.event_time ? 'error' : ''}
              />
              {errors.event_time && (
                <span className="error-message">{errors.event_time}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update Exhibition' : 'Create Exhibition')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExhibitionForm;
